import { useCallback, useState } from 'react';
import {
  buscarCategoriasLiga,
  buscarCategoriaLigaPorId,
  criarCategoriaLiga,
  atualizarCategoriaLiga,
  deletarCategoriaLiga,
  buscarCategoriasTorneioPorCategoriaLiga,
} from '../services/categoriaLigaService';
import type { CategoriaLigaInput } from '../types/categoriaLiga';

/**
 * Hook fino sobre `categoriaLigaService.ts` — mirror de `useLiga` (`executeWithLoading`).
 *
 * PRPs/plans/categoria-liga-phase-3-frontend.plan.md — Task 4 (Satisfies AC-A1).
 */
export function useCategoriaLiga() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeWithLoading = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err: any) {
      const message = err?.message || 'Erro inesperado';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const listar = useCallback(
    (ligaId: string) => executeWithLoading(() => buscarCategoriasLiga(ligaId)),
    [executeWithLoading],
  );

  const buscarPorId = useCallback(
    (id: string) => executeWithLoading(() => buscarCategoriaLigaPorId(id)),
    [executeWithLoading],
  );

  const criar = useCallback(
    (input: CategoriaLigaInput) => executeWithLoading(() => criarCategoriaLiga(input)),
    [executeWithLoading],
  );

  const atualizar = useCallback(
    (id: string, input: CategoriaLigaInput) =>
      executeWithLoading(() => atualizarCategoriaLiga(id, input)),
    [executeWithLoading],
  );

  const deletar = useCallback(
    (id: string) => executeWithLoading(() => deletarCategoriaLiga(id)),
    [executeWithLoading],
  );

  const listarInstancias = useCallback(
    (categoriaLigaId: string) =>
      executeWithLoading(() => buscarCategoriasTorneioPorCategoriaLiga(categoriaLigaId)),
    [executeWithLoading],
  );

  return {
    loading,
    error,
    listar,
    buscarPorId,
    criar,
    atualizar,
    deletar,
    listarInstancias,
  } as const;
}

export default useCategoriaLiga;
